/**
 * OrphanSweepPage — Iter182++
 *
 * Admin maintenance tool. Scans the DB for child records whose
 * parent reference points at a non-existent document (typically
 * the result of historical "force delete" client/property
 * operations), and exposes per-bucket bulk-cleanup actions.
 *
 * Auth: admin-only (the API endpoints enforce require_admin).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Trash2, RefreshCcw, AlertTriangle, CheckCircle2, ShieldAlert, Ruler, Wrench } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export default function OrphanSweepPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/orphans');
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to scan');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const totalSelected = report
    ? report.buckets.filter(b => selected.has(b.key)).reduce((a, b) => a + b.count, 0)
    : 0;

  const runCleanup = async (mode) => {
    if (totalSelected === 0) {
      toast.error('Tick at least one bucket first.');
      return;
    }
    if (mode === 'hard' && confirmText.trim().toLowerCase() !== 'delete') {
      toast.error('Type "delete" to confirm hard delete.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/orphans/cleanup', {
        buckets: Array.from(selected),
        mode,
      });
      const lines = data.summary.map(s => `${s.label}: ${s.deleted}${s.errors ? ` (${s.errors} errors)` : ''}`);
      toast.success(`Cleaned up ${data.total_deleted} record${data.total_deleted === 1 ? '' : 's'}`);
      console.log('[orphan-sweep] cleanup result:', lines.join('\n'));
      setSelected(new Set());
      setConfirmText('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="orphan-sweep-page" className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 font-[Manrope]">
          <ShieldAlert size={22} className="text-[#D35400]" /> Orphan Sweep
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Maintenance tool — finds dependent records whose parent has been hard-deleted (typically a result of historical "Force delete" actions). Pick the buckets you want to clean up and choose Soft or Hard delete.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          data-testid="orphan-sweep-refresh"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 bg-white rounded text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCcw size={13} />}
          Re-scan
        </button>
        {report && (
          <div className={`text-sm font-semibold ${report.total_orphans > 0 ? 'text-amber-700' : 'text-emerald-700'} flex items-center gap-1.5`}>
            {report.total_orphans > 0 ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
            {report.total_orphans} orphan{report.total_orphans === 1 ? '' : 's'} across {report.buckets.length} buckets
          </div>
        )}
      </div>

      {!loading && report && (
        <div className="space-y-2">
          {report.buckets.map(b => (
            <div
              key={b.key}
              data-testid={`orphan-bucket-${b.key}`}
              className={`border rounded p-3 transition-colors ${
                b.count === 0 ? 'border-slate-200 bg-slate-50 opacity-60' :
                selected.has(b.key) ? 'border-sky-500 bg-sky-50' : 'border-amber-300 bg-amber-50/40'
              }`}
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`orphan-check-${b.key}`}
                  checked={selected.has(b.key)}
                  onChange={() => toggle(b.key)}
                  disabled={b.count === 0}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <strong className="text-slate-900">{b.label}</strong>
                    <span className={`text-xs font-mono px-2 py-0.5 rounded ${b.count === 0 ? 'bg-slate-200 text-slate-500' : 'bg-amber-200 text-amber-900'}`}>
                      {b.count} record{b.count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {b.collection}.{b.ref} → {b.parent} (parent missing)
                  </div>
                  {b.sample.length > 0 && (
                    <ul className="mt-1.5 text-[11px] font-mono space-y-0.5 text-slate-700 bg-white/60 border border-slate-200 rounded px-2 py-1">
                      {b.sample.map(s => (
                        <li key={s._id} className="truncate">
                          {s._id}
                          {s.appraisal_no ? ` · A${s.appraisal_no}` : ''}
                          {s.property_address_street ? ` · ${s.property_address_num || ''} ${s.property_address_street}` : ''}
                          {s.filename ? ` · ${s.filename}` : ''}
                          {s.is_deleted ? ' · soft-deleted' : ''}
                        </li>
                      ))}
                      {b.count > b.sample.length && (
                        <li className="text-slate-400">… and {b.count - b.sample.length} more</li>
                      )}
                    </ul>
                  )}
                </div>
              </label>
            </div>
          ))}
        </div>
      )}

      {totalSelected > 0 && (
        <div data-testid="orphan-sweep-actions" className="sticky bottom-4 bg-slate-900 text-white rounded-lg shadow-2xl px-4 py-3 space-y-2 border border-slate-700">
          <div className="text-sm">
            <strong className="text-sky-300">{totalSelected}</strong> record{totalSelected === 1 ? '' : 's'} selected across {selected.size} bucket{selected.size === 1 ? '' : 's'}.
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="orphan-sweep-soft"
              onClick={() => runCleanup('soft')}
              disabled={busy}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Soft delete (where supported)
            </button>
            <div className="flex items-center gap-1.5">
              <input
                data-testid="orphan-sweep-confirm-input"
                placeholder='Type "delete" to confirm'
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="text-xs bg-slate-800 border border-slate-600 rounded px-2 py-1 placeholder-slate-500 w-44"
              />
              <button
                data-testid="orphan-sweep-hard"
                onClick={() => runCleanup('hard')}
                disabled={busy || confirmText.trim().toLowerCase() !== 'delete'}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold disabled:opacity-40"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Hard delete
              </button>
            </div>
            <button
              type="button"
              onClick={() => { setSelected(new Set()); setConfirmText(''); }}
              className="text-xs text-slate-300 hover:text-white px-2 py-1 rounded border border-slate-600 hover:border-slate-400"
            >Clear selection</button>
          </div>
        </div>
      )}

      {/* Iter185 — Roof template aspect audit. Diagnostic-only: surfaces
          templates whose stored canvas dims (width/height) drift from
          the rasterised image's natural pixel dims. The annotator now
          auto-aligns at render time, but this audit lets admins spot
          legacy templates that may still need a manual re-trace if
          they want stroke geometry to match the image perfectly. */}
      <AspectAuditPanel />
    </div>
  );
}

// ─── Iter185: aspect-audit panel ────────────────────────────────────────
function AspectAuditPanel() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [fixingId, setFixingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/roof-templates/aspect-audit');
      setItems(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Aspect audit failed');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const fixCanvas = async (id) => {
    setFixingId(id);
    try {
      await api.post(`/admin/roof-templates/${id}/fix-canvas-dims`);
      toast.success('Canvas dims aligned to image');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Fix failed');
    } finally {
      setFixingId(null);
    }
  };

  return (
    <div data-testid="aspect-audit-panel" className="space-y-2 pt-6 border-t border-slate-200">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-[Manrope]">
          <Ruler size={18} className="text-[#D35400]" /> Roof template aspect audit
        </h2>
        <button
          data-testid="aspect-audit-refresh"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 bg-white rounded text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
          Re-scan
        </button>
      </div>
      <p className="text-xs text-slate-500">
        Diagnostic only — surfaces templates whose stored canvas dims drift from the image's natural pixel size, or whose canvas dims are missing. The annotator now auto-aligns at render time, so flagged templates still display correctly; the "Align canvas to image" button is for admins who want the stored stroke coords themselves to match the image.
      </p>
      {!loading && items && items.flagged_count === 0 && (
        <div data-testid="aspect-audit-clean" className="flex items-center gap-1.5 text-emerald-700 font-semibold text-sm">
          <CheckCircle2 size={14} /> All templates are clean — image and canvas dims match.
        </div>
      )}
      {!loading && items && items.flagged_count > 0 && (
        <div className="space-y-1.5">
          {items.items.map(it => (
            <div
              key={it.id}
              data-testid={`aspect-audit-row-${it.id}`}
              className="flex items-center gap-2 p-2 rounded border border-amber-300 bg-amber-50/40 text-xs"
            >
              <AlertTriangle size={14} className="text-amber-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{it.name}</div>
                <div className="text-[11px] text-slate-600 font-mono">
                  {it.issue === 'aspect_mismatch' && (
                    <>canvas {it.canvas_w}×{it.canvas_h} ({it.canvas_aspect}) vs image {it.image_w}×{it.image_h} ({it.image_aspect}) — drift {it.drift_pct}%</>
                  )}
                  {it.issue === 'missing_canvas_dims' && (
                    <>image {it.image_w}×{it.image_h} · canvas dims missing · {it.stroke_count} strokes</>
                  )}
                  {it.issue === 'no_image' && <>no image stored</>}
                  {it.issue === 'image_decode_failed' && <>image decode failed: {it.detail}</>}
                </div>
              </div>
              {(it.issue === 'aspect_mismatch' || it.issue === 'missing_canvas_dims') && (
                <button
                  data-testid={`aspect-audit-fix-${it.id}`}
                  onClick={() => fixCanvas(it.id)}
                  disabled={fixingId === it.id}
                  className="flex items-center gap-1 px-2 py-1 rounded bg-[#D35400] hover:bg-[#A04000] text-white text-[11px] font-semibold disabled:opacity-50"
                  title="Overwrite the template's stored canvas width/height with the image's natural pixel size"
                >
                  {fixingId === it.id ? <Loader2 size={10} className="animate-spin" /> : <Wrench size={10} />}
                  Align canvas to image
                </button>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
}